import React, { useEffect } from 'react';
import { LoadMoreButton, ProductCard } from '@components';
import { Typography } from 'gd-design-library';
import { Product } from '@services';
import classNames from 'classnames/bind';
import styles from './ProductsList.module.scss';
import { ProductCardBaseSkeleton } from 'components/ProductCardBase';
import { LOADING_PRODUCTS_SKELETONS_COUNT } from '@constants';
import { LineItemDraft } from '@commercetools/platform-sdk';
import { AnalyticsListContext, pushViewItemListEvent } from '@utils';
import { BestMatchProductCard } from './BestMatchProductCard';

interface Props {
  products: Product[]
  loadMore: () => Promise<unknown>
  currentCount: number
  total: number
  message?: string
  isLoading?: boolean
  search?: boolean
  handleAddToCart: (lineItemDraft: LineItemDraft, currency?: string) => Promise<void>;
  isNarrowedSearch?: boolean;
  analyticsContext: AnalyticsListContext;
  bestMatchId?: string;
}

const CN = 'container';
const itemsClassName = `${CN}--items`;
const productCardClassName = `${itemsClassName}--product-card`;
const productCardBaseClassName = `${productCardClassName}--base`;
const noItemsClassName = `${itemsClassName}--no-items`;
const loadMoreButtonClassName = `${CN}--load-more-button`;
const cx = classNames.bind(styles);

export const ProductsList: React.FC<Props> = ({
  products,
  loadMore,
  currentCount,
  total,
  isLoading,
  message = 'No results found.',
  search = false,
  handleAddToCart,
  analyticsContext,
  bestMatchId
}) => {
  useEffect(() => {
    pushViewItemListEvent(products, analyticsContext);
  }, [ products, analyticsContext?.listId, analyticsContext?.listName ]);

  const bestMatchProduct = bestMatchId ? products.find(p => p.id === bestMatchId) : undefined;
  const remainingProducts = bestMatchProduct ? products.filter(p => p.id !== bestMatchId) : products;

  return (
    <>
      <div className={ cx(itemsClassName) }>
        {products.length ?
          <>
            {bestMatchProduct && (
              <BestMatchProductCard
                product={ bestMatchProduct }
                handleAddToCart={ handleAddToCart }
                analyticsContext={ analyticsContext }
              />
            )}
            {remainingProducts.map((product, i) => (
              <ProductCard
                className={ cx(productCardClassName) }
                baseClassName={ cx(productCardBaseClassName) }
                position={ i }
                key={ product.id }
                product={ product }
                isLoading={ isLoading }
                search={ search }
                handleAddToCart={ handleAddToCart }
                analyticsContext={ analyticsContext }
              />
            ))}
          </> : (
            !isLoading ? (
              <Typography
                variant='h6'
                className={ cx(noItemsClassName) }
              >
                {message}
              </Typography>
            ) : Array.from(Array(LOADING_PRODUCTS_SKELETONS_COUNT).keys()).map(index => <ProductCardBaseSkeleton key={ index }/>)
          )
        }
      </div>
      <LoadMoreButton
        className={ cx(loadMoreButtonClassName) }
        currentCount={ currentCount }
        total={ total }
        onClick={ loadMore }
      />
    </>
  );
};
